import { FC, useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { font, colors } from "../../lib/sharedStyles";
import PLPFilterIcon from "../Icons/PLPFilterIcon";
import ChevronDown from "../Icons/ChevronDown";
import PLPSortMenu from "./PLPSortMenu";
import { useSearchspringPLPContext } from "../../contexts/SearchspringPLPContext";
import PLPViewSelector from "./PLPViewSelector";
import PLPCompareToggle from "./PLPCompareToggle";
import PLPFilterDesktop from "./PLPFilterDesktop";
import PLPActiveFilters from "./PLPActiveFilters";

const Root = styled.div`
  width: 100%;
  height: 88px;
  
  button {
    appearance: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
  }
`;

const StickyContainer = styled.div<{ $isStuck: boolean; $top: number; }>`
  position: relative;
  width: 100%;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 16px;
  background-color: ${colors.fender.white};
  border-top: 1px solid ${colors.fender.grey03};
  // Transform is from the Shopify theme code, to match motion.
  transition: height 250ms ease, transform 600ms cubic-bezier(.38,.41,.27,1);

  @media (min-width: 1024px) {
    padding: 40px 72px;
  }

  ${({ $isStuck, $top }) => $isStuck && `
    position: fixed;
    z-index: 12;
    top: 0;
    left: 0;
    height: 64px;
    transform: translateY(${$top}px);
    @media (max-width: 1023px) {
      padding-top: 20px;
      padding-bottom: 20px;
    }
  `}
`;

const ResultsCount = styled.p<{ $hide?: string; }>`
  ${font('Instrument Sans', 600, '12px', '18px')}
  color: ${colors.fender.primaryDark};
  letter-spacing: 0.12px;
  margin: 0;

  @media (min-width: 1024px) {
    ${font('Instrument Sans', 600, '16px', '12px')}
    margin-left: 32px;
  }

  ${({ $hide }) => $hide == 'mobile' && `
    display: none;
    @media (min-width: 1024px) {
      display: block;
    }
  `}

  ${({ $hide }) => $hide == 'desktop' && `
    @media (min-width: 1024px) {
      display: none;
    }
  `}
`;

const Label = styled.p`
  ${font('Instrument Sans', 400, '12px', '18px')}
  color: ${colors.fender.primaryDark};
  letter-spacing: 0.12px;
  margin: 0;
`;

const MenuGroup = styled.div`
  display: flex;
  align-items: center;
`;

const ViewOptions = styled.div`
  display: flex;
`;

const CompareContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;

  @media (min-width: 1024px) {
    margin-left: 32px;
  }
`;

const FilterButton = styled.button`
  display: none;

  > p {
    ${font('Instrument Sans', 600, '16px', '12px')}
    margin-left: 8px;
  }

  @media (min-width: 1024px) {
    display: flex;
    align-items: center;
  }
`;

const SortTrigger = styled.div<{ $isOpen: boolean; }>`
  cursor: pointer;
  position: relative;
  display: none;
  margin-right: 32px;
  padding: 0;

  > svg {
    margin-top: 4px;
    margin-left: 8px;
    transition: transform 250ms ease;
    ${({ $isOpen }) => $isOpen && `
      transform: rotate(180deg);
    `}
  }

  @media (min-width: 1024px) {
    display: flex;
    align-items: center;
  }
`;

const DesktopActiveFilterWrap = styled.div`
  display: none;

  @media (min-width: 1024px) {
    display: flex;
  }
`;

interface SearchspringPLPMenuProps {
  belowGridRef: React.RefObject<HTMLDivElement>;
  isSearchTemplate: boolean;
}

const SearchspringPLPMenu: FC<SearchspringPLPMenuProps> = ({ belowGridRef, isSearchTemplate }) => {
  // This is set to a fixed value in the styles above, so no need to calculate at runtime.
  const PLPMenuHeight = 88;
  const rootRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);
  const [fixedTopPos, setFixedTopPos] = useState(0);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  const [prevScrollY, setPrevScrollY] = useState(window.scrollY);
  const { 
    activeFilters,
    filterMenuOpen, 
    pagination, 
    sortMenuOpen, 
    toggleFilterMenu, 
    toggleSortMenu ,
    collectionCompareEnable
  } = useSearchspringPLPContext();

  useEffect(() => {
    const stickyHeader: HTMLElement|null = document.querySelector('sticky-header');
    setStickyHeaderHeight(stickyHeader?.offsetHeight || 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => { window.removeEventListener('scroll', handleScroll); }
  });

  const handleScroll = () => {
    let scrolledPastMenu = false;
    let scrolledPastGrid = false;

    if (rootRef && rootRef.current) {
      const rootRect = rootRef.current.getBoundingClientRect();
      const restingTopPosition = rootRect.top;

      if (restingTopPosition < 0) {
        scrolledPastMenu = true;
      }
    }

    if (belowGridRef && belowGridRef.current) {
      const belowGridRect = belowGridRef.current.getBoundingClientRect();
      const belowGridContentDistanceFromViewportTop = belowGridRect.top;

      if (belowGridContentDistanceFromViewportTop - PLPMenuHeight < 0) {
        scrolledPastGrid = true;
      }
    }

    // Fix the menu to the top of the viewport when scrolled past its 
    // static position until it reaches the bottom of the grid.
    if (scrolledPastMenu && !scrolledPastGrid) {
      setIsStuck(true);
    } else {
      setIsStuck(false);
    }

    // The header gets fixed to the top of the viewport when scrolling up,
    // so the fixed position of the menu needs to account for that.
    const isScrollingUp = window.scrollY < prevScrollY;
    const fixedTopPos = (isScrollingUp) ? stickyHeaderHeight : 0;
    setFixedTopPos(fixedTopPos);
    setPrevScrollY(window.scrollY);
  } 

  const resultsCountText = `${pagination.totalResults || 0} Results`;
  let filtersLabel = (filterMenuOpen) ? 'Hide Filters' : 'Filters';
  if (activeFilters.length > 0) {
    filtersLabel += ` (${activeFilters.length})`;
  }

  return (
    <Root ref={rootRef}>
      <StickyContainer $isStuck={isStuck} $top={fixedTopPos}>
        <PLPFilterDesktop />
        <ResultsCount $hide="desktop">
          {resultsCountText}
        </ResultsCount>
        <MenuGroup>
          <FilterButton onClick={toggleFilterMenu}>
            <PLPFilterIcon />
            <Label>{filtersLabel}</Label>
          </FilterButton>
          <DesktopActiveFilterWrap>
            <PLPActiveFilters />
          </DesktopActiveFilterWrap>
        </MenuGroup>
        <MenuGroup>
          <SortTrigger onClick={toggleSortMenu} $isOpen={sortMenuOpen}>
            <Label>Sort by</Label>
            <ChevronDown color={colors.fender.primaryDark} />
            <PLPSortMenu />
          </SortTrigger>
          <ViewOptions>
            <Label>View</Label>
            <PLPViewSelector />
          </ViewOptions>
          {(!isSearchTemplate) && (collectionCompareEnable && collectionCompareEnable == 'true') && (
            <CompareContainer>
              <Label>Compare</Label>
              <PLPCompareToggle />
            </CompareContainer>
          )}
          <ResultsCount $hide="mobile">
            {resultsCountText}
          </ResultsCount>
        </MenuGroup>
      </StickyContainer>
    </Root>
  );
}

export default SearchspringPLPMenu;